import { NetworkId } from '@mls-wallet/shared-types';
import { EthereumIcon } from '@/components/icons/EthereumIcon';
import { PolygonIcon } from '@/components/icons/PolygonIcon';
import { ArbitrumIcon } from '@/components/icons/ArbitrumIcon';
import { BaseIcon } from '@/components/icons/BaseIcon';
import { RoninIcon } from '../icons/RoninIcon';

/**
 * Props for the NetworkIcon component
 */
interface NetworkIconProps {
  networkId: number;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * Shared component for rendering network-specific icons
 *
 * Supports the following networks:
 * - Ethereum
 * - Polygon
 * - Optimism
 * - Arbitrum
 * - Base
 * - Avalanche
 * - Ronin
 * - Smaug Testnet and fork networks
 */
export function NetworkIcon({
  networkId,
  className = '',
  size = 'md',
}: NetworkIconProps) {
  // Size mapping for consistent sizing across the app
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const sizeClass = sizeClasses[size];
  const combinedClassName = `${sizeClass} ${className}`;

  switch (networkId) {
    case NetworkId.ETHEREUM:
      return <EthereumIcon className={combinedClassName} />;
    case NetworkId.POLYGON:
      return <PolygonIcon className={combinedClassName} />;
    case NetworkId.OPTIMISM:
      // TODO: Add Optimism icon when available
      return <span className={`text-sm ${className}`}>○</span>;
    case NetworkId.ARBITRUM:
      return <ArbitrumIcon className={combinedClassName} />;
    case NetworkId.ARBITRUM_FORK:
      return <ArbitrumIcon className={combinedClassName} />;
    case NetworkId.BASE:
      return <BaseIcon className={combinedClassName} />;
    case NetworkId.BASE_FORK:
    case NetworkId.SMAUG:
      return <BaseIcon className={combinedClassName} />;
    case NetworkId.ETHEREUM_FORK:
    case NetworkId.ETHEREUM_CONTRACT_DEPLOYMENT_FORK:
    case NetworkId.ETHEREUM_SEPOLIA:
      return <EthereumIcon className={combinedClassName} />;
    case NetworkId.AVALANCHE:
      // TODO: Add Avalanche icon when available
      return <span className={`text-sm ${className}`}>▲</span>;
    case NetworkId.RONIN:
    case NetworkId.RONIN_FORK:
      return <RoninIcon className={combinedClassName} />;
    default:
      return null;
  }
}
